import { useCallback, useMemo } from "react";
import { IntlShape, useIntl } from "react-intl";
import { TabItemData } from "@/components/molecules/tabListView";
import { TabType } from "@/common/stores/persistingStores/navigation";

interface UseTabItemsProps {
	activeInteractionsCount: number;
	agentCanViewAllInteractions: boolean;
	isQueueSupervisor: boolean | undefined;
	agentHasOperatorAgentViewPermission: boolean;
}

const TabTypeTranslationKey = {
	[TabType.Interactions]: "Interactions",
	[TabType.SupervisorInteractions]: "SupervisorInteractions",
	[TabType.Supervisor]: "Supervisor",
	[TabType.History]: "History",
	[TabType.Voicemail]: "Voicemail",
};

export const getTabTitle = (tab: TabType, intl: IntlShape) => {
	return intl.formatMessage({
		id: `Navigation.${TabTypeTranslationKey[tab]}`,
	});
};

export const useTabTitle = () => {
	const intl = useIntl();

	const getTitle = useCallback((tab: TabType) => {
		return getTabTitle(tab, intl);
	}, []);

	return getTitle;
};

export const useTabItems = ({
	activeInteractionsCount,
	agentCanViewAllInteractions,
	isQueueSupervisor,
	agentHasOperatorAgentViewPermission,
}: UseTabItemsProps): TabItemData[] => {
	const intl = useIntl();

	const tabItem = useCallback((tab: TabType) => {
		return {
			key: tab,
			title: getTabTitle(tab, intl),
		};
	}, []);

	return useMemo<TabItemData[]>(() => {
		const items: TabItemData[] = [
			{
				...tabItem(TabType.Interactions),
				subTitle:
					activeInteractionsCount > 0
						? `${activeInteractionsCount}`
						: undefined,
			},
		];

		if (agentCanViewAllInteractions) {
			items.push(tabItem(TabType.SupervisorInteractions));
		}

		if (isQueueSupervisor || agentHasOperatorAgentViewPermission) {
			items.push(tabItem(TabType.Supervisor));
		}

		items.push(tabItem(TabType.History));

		return items;
	}, [
		activeInteractionsCount,
		agentCanViewAllInteractions,
		isQueueSupervisor,
		agentHasOperatorAgentViewPermission,
		intl,
	]);
};
